/**
 * Binance WebSocket adapter — public streams, no auth required.
 *
 * Futures:  wss://fstream.binance.com/market/ws/<symbol>@markPrice@1s
 *           (mark price and other “regular” market streams use `/market`;
 *           the legacy root `/ws` path may connect without pushing data.)
 * Spot:     wss://stream.binance.com:9443/ws/<symbol>@bookTicker
 *           (used as fallback when futuresOnly=false and token is spot-only)
 */

import type {
  ExchangeWsAdapter,
  PriceUpdateCallback,
} from "../live-pricing.types";

const RECONNECT_DELAYS_MS = [2_000, 5_000, 10_000, 20_000, 30_000];

function normalizeSymbol(symbol: string): string {
  const upper = symbol.toUpperCase();
  return upper.endsWith("USDT") ? upper : `${upper}USDT`;
}

function makeAdapter(
  buildUrl: (symbol: string) => string,
  parseMessage: (
    raw: string,
    symbol: string
  ) => Parameters<PriceUpdateCallback>[0] | null
): ExchangeWsAdapter {
  return {
    subscribe(symbol, onUpdate, onError) {
      const normalized = normalizeSymbol(symbol);
      let ws: WebSocket | null = null;
      let attempt = 0;
      let stopped = false;

      function connect() {
        if (stopped) return;
        ws = new WebSocket(buildUrl(normalized));

        ws.onmessage = (e: MessageEvent<string>) => {
          try {
            const update = parseMessage(e.data, normalized);
            if (update) {
              attempt = 0;
              onUpdate(update);
            }
          } catch {
            // ignore parse errors
          }
        };

        ws.onerror = () => {
          // error always followed by close — let onclose handle reconnect
        };

        ws.onclose = () => {
          if (stopped) return;
          const delay =
            RECONNECT_DELAYS_MS[
              Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)
            ];
          attempt++;
          if (attempt > RECONNECT_DELAYS_MS.length) {
            onError?.(new Error(`Binance WS closed after ${attempt} attempts`));
            return;
          }
          setTimeout(connect, delay);
        };
      }

      connect();

      return () => {
        stopped = true;
        ws?.close();
        ws = null;
      };
    },
  };
}

interface BinanceMarkPriceMsg {
  e: string;
  E: number;
  s: string;
  p: string;
  i: string;
  r: string;
  T: number;
}

export const binanceFuturesWsAdapter: ExchangeWsAdapter = makeAdapter(
  (symbol) =>
    `wss://fstream.binance.com/market/ws/${symbol.toLowerCase()}@markPrice@1s`,
  (raw, symbol) => {
    const msg = JSON.parse(raw) as BinanceMarkPriceMsg;
    if (msg.e !== "markPriceUpdate") return null;
    return {
      symbol,
      exchange: "binance",
      markPrice: Number.parseFloat(msg.p),
      indexPrice: Number.parseFloat(msg.i),
      fundingRate: Number.parseFloat(msg.r),
      nextFundingTime: msg.T,
      timestamp: msg.E,
    };
  }
);

interface BinanceBookTickerMsg {
  s: string;
  b: string;
  B: string;
  a: string;
  A: string;
}

export const binanceSpotWsAdapter: ExchangeWsAdapter = makeAdapter(
  (symbol) =>
    `wss://stream.binance.com:9443/ws/${symbol.toLowerCase()}@bookTicker`,
  (raw, symbol) => {
    const msg = JSON.parse(raw) as BinanceBookTickerMsg;
    const bid = Number.parseFloat(msg.b);
    const ask = Number.parseFloat(msg.a);
    const mid = (bid + ask) / 2;
    return {
      symbol,
      exchange: "binance",
      markPrice: mid,
      indexPrice: mid,
      fundingRate: 0,
      nextFundingTime: 0,
      timestamp: Date.now(),
    };
  }
);
